<template>
    <div
        v-if="!isPending && !isError"
        class="component-selector"
    >
        <FormSingleSelectField
            v-for="componentSelector in componentSelectorsRef"
            :key="componentSelector.name"
            v-model="componentSelector.modelValue"
            :label="componentSelector.labelKey && t(componentSelector.labelKey)"
            :placeholder-text="componentSelector.placeholderTextKey && t(componentSelector.placeholderTextKey)"
            :options="componentSelector.options"
            :disabled="componentSelector.disabled"
            class="component-selector__field"
            @update:model-value="emit('updateValue', {[componentSelector.name]: componentSelector.modelValue})"
        >
            <template v-if="componentSelector.isLastChild" #optionTrailing="{ option }">
                <pbl-icon
                    v-if="option.extraData?.isBookmarked"
                    class="component-selector__trailing-icon"
                    name="star-fill"
                />
            </template>
        </FormSingleSelectField>
        <div class="component-selector__buttons">
            <HorizontalButton
                :text="t('transformers.conditionStatus.selector.actions.reset')"
                :disabled="!isAbleToReset"
                icon-name="arrow-counterclockwise"
                @click="onSelectionReset"
            />
            <HorizontalButton
                v-if="modelValue"
                :text="t('transformers.conditionStatus.selector.actions.favourite')"
                :icon-name="!!bookmarkId ? 'star-fill' : 'star'"
                :variant="!!bookmarkId ? 'primary' : 'secondary'"
                :loading="isPending || isAddPending || isDeletePending"
                @click="onFavouriteClicked"
            />
        </div>
    </div>
    <div
        v-else-if="isPending"
        class="loading-state"
    >
        <pbl-spinner />
        <span>
            {{ t('transformers.conditionStatus.selector.loading') }}
        </span>
    </div>
    <div
        v-else-if="isError"
        class="error-state"
    >
        <pbl-icon name="exclamation-triangle" />
        <span>
            {{ t('transformers.conditionStatus.selector.error') }}
        </span>
    </div>
</template>

<script setup lang="ts">
import FormSingleSelectField from '@/components/form-inputs/form-single-select-field/FormSingleSelectField.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import HorizontalButton from '@/components/buttons/HorizontalButton.vue'
import { useBookmarksAddMutation } from '@/api/mutations/bookmarks/useBookmarksAddMutation'
import { ComponentType } from '@/configuration/component/component-types'
import { useBookmarksDeleteMutation } from '@/api/mutations/bookmarks/useBookmarksDeleteMutation'
import { AxiosError } from 'axios'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { ComponentSelector } from '@/configuration/component/component-selector/component-selector-configuration'

const props = defineProps<{
    componentType: ComponentType,
    componentSelectors: ComponentSelector[]
    isPending?: boolean
    isError?: boolean
    bookmarkId?: number | null
}>()

const emit = defineEmits<{
    'updateValue': [payload: { [key: string]: string | number | undefined }]
    'resetValues': []
}>()

const componentSelectorsRef = computed(() => props.componentSelectors)

const toasts = usePebbleToasts()
const { t } = useI18n()

const { mutateAsync: mutateAddAsync, isPending: isAddPending } = useBookmarksAddMutation()
const { mutateAsync: mutateDeleteAsync, isPending: isDeletePending } = useBookmarksDeleteMutation()

const lastChild = computed(() => componentSelectorsRef.value.find((selector) => selector.isLastChild))
const modelValue = computed(() => lastChild.value?.modelValue)

const isAbleToReset = computed(() => componentSelectorsRef.value.filter((selector) => selector.modelValue !== undefined && selector.modelValue !== null).length !== 0)

const onSelectionReset = () => {
    emit('resetValues')
}

const onFavouriteClicked = async () => {
    if (isAddPending.value || isDeletePending.value) {
        return
    }

    try {
        if (props.bookmarkId) {
            await mutateDeleteAsync({
                bookmarkId: Number(props.bookmarkId),
            })
        } else {
            await mutateAddAsync({
                bookmarkData: {
                    componentSid: modelValue.value ? modelValue.value.toString() : undefined,
                    componentType: props.componentType,
                },
            })
        }
    } catch (err) {
        if (err instanceof AxiosError) {
            toasts?.error({
                message: t('common.errors.unknown'),
                duration: 3000,
            })
        }
    }
}
</script>

<style scoped lang="scss">
.component-selector {
    display: flex;
    flex-direction: row;

    align-items: center;
    gap: $spacing-s;

    &__field {
        min-width: 300px;
    }

    &__buttons {
        display: flex;
        gap: $spacing-s;

        margin-top: 1.6rem;
        margin-left: $spacing-2xs;
    }

    &__trailing-icon {
        color: $pbl-primary;
    }
}

.loading-state {
    display: flex;

    align-items: center;
    gap: $spacing-xs;

    pbl-spinner {
        --size: 1.5rem;
    }
}

.error-state {
    display: flex;

    align-items: center;
    gap: $spacing-2xs;

    color: $pbl-invalid;

    pbl-icon {
        --size: 1.5rem;

        color: $pbl-invalid-contrast;
    }
}
</style>
